import { FC, memo, useCallback, useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";
import { useNarrativesContext } from "@components/layout/main-layout/private-layout";
import AdvisorForm from "@components/pages/project/single-functions/project-structuring/form";
import CommonFooterButton from "@commonComponents/footer-button";
import { useStore } from "@stores/root-store";
import { constRoute } from "@utils/route";
import { useForm } from "antd/es/form/Form";
import { useNavigate, useParams } from "react-router-dom";
import { io } from "socket.io-client";
import { DataType } from "../../../../../../types/data-type";
import { baseSocketUrl } from "@api/const";
import { notification } from "@utils/notifications";

interface AdvisorFormProps {}
const formQuestions = [
  {
    q: <span>Tell Us about the Project below:</span>,
    result: <span>Your Results Below:</span>,
    limit: 1000,
    placeholder:
      "Please provide the full text of your current mitigation rationale.\n\n" +
      "For the most helpful analysis, ensure your text includes your best attempt at describing:\n\n" +
      " • The Project's Action: What is the core activity that reduces GHG emissions? (e.g., installing solar panels, improving energy efficiency).\n\n" +
      " • The 'Before' Scenario: What is the current, high-emission situation (the baseline) that the project will change?\n\n" +
      " • Key Numbers: What are the main assumptions and data? (e.g., estimated tCO2e reduced, emission factors, project lifetime).\n\n" +
      " • The Need for GCF: Why won't this project happen without GCF funding? (e.g., high costs, technology risk).\n\n" +
      " • The Monitoring Plan: How will you track and verify the emission reductions over time?\n\n" +
      "Even a brief draft covering these points will provide a strong basis for the analysis and enhancement.",
  },
  {
    q: <>Tell Us About the Project’s Feasibility and Site Assessment Below:</>,
    result: <>Your Results Below:</>,
    limit: 1000,
    placeholder:
      "Please provide the full text of your current Feasibility and Site Assessment section. For the most helpful analysis, ensure \nyour text includes your best attempt at describing:\n\n" +
      " • Site and Resource: Where is the project located and why was this site chosen? Include any available data on the renewable resource (e.g.,\n\u00A0\u00A0\u00A0 solar irradiation, wind speed).\n\n" +
      " • Technology and Design: What specific technology will be used (e.g., monocrystalline solar panels, direct-drive wind turbines)? What is the\n\u00A0\u00A0\u00A0 proposed capacity (MW) and basic layout?\n\n" +
      " • Grid Connection: How and where will the project connect to the electrical grid? Mention the nearest substation and its distance.\n\n" +
      " • Permitting and Social Factors: What is the status of key permits, the Environmental and Social Impact Assessment (ESIA), and any\n\u00A0\u00A0\u00A0 community consultations?\n\n" +
      "Even a brief draft covering these points will provide a strong basis for the analysis and enhancement.",
  },
  {
    q: <>Tell Us About the project’s Financial and Economic Condition Below:</>,
    result: <>Your Results Below:</>,
    limit: 1000,
    placeholder:
      "Please provide the full text of your current Financial and Economic Analysis section. For the most helpful analysis, ensure \nyour text includes your best attempt at describing:\n\n" +
      " • Project Costs: What are the total capital expenditures (CAPEX) and estimated annual operating expenditures (OPEX)?\n\n" +
      " • Revenue and Financing: How will the project generate revenue (e.g., tariffs, sales)? How will it be financed (e.g., GCF loan, sponsor equity,\n\u00A0\u00A0\u00A0 commercial debt)?\n\n" +
      " • Financial Performance: What are the key financial metrics, even if preliminary? (e.g., Internal Rate of Return - IRR, Net Present Value - NPV).\n\n" +
      " • Socioeconomic Benefits: What are the wider economic impacts of the project? (e.g., number of jobs created, local economic stimulus).\n\n" +
      "Even a brief draft covering these points will provide a strong basis for the analysis and enhancement.",
  },
  {
    q: (
      <>
        Tell Us About The Project’s Regulatory and Compliance framework below:
      </>
    ),
    result: <>Your Results Below:</>,
    placeholder:
      "Please provide the full text of your current Regulatory and Compliance section.\n\n" +
      "For the most helpful analysis, ensure your text includes your best attempt at describing:\n\n" +
      " • Regulatory Alignment: Which key national policies (e.g., NDC, National Climate Change Act) and local laws does the project adhere to?\n\n" +
      " • Required Permits: What are the specific environmental, social, and land-use permits needed for the project?\n\n" +
      " • Compliance Management: How will compliance be monitored and managed? Who will be responsible for overseeing it?\n\n" +
      " • Key Risks: What are the main regulatory risks (e.g., permit delays, policy changes) and how do you plan to mitigate them?\n\n" +
      "Even a brief draft covering these points will provide a strong basis for the analysis and enhancement.",
    limit: 1000,
  },
  {
    q: <>Tell Us About The Project’s Implementation and Timeline Below:</>,
    result: <>Your Results Below:</>,
    limit: 1000,
    placeholder:
      "Please provide the full text of your current Project Implementation Plan section.\n\n" +
      "For the most helpful analysis, ensure your text includes your best attempt at describing:\n\n" +
      " • Phases and Timeline: What are the main stages of the project (e.g., inception, implementation, closure) and what is the overall timeline?\n\n" +
      " • Management and Resources: Who will be responsible for implementation (e.g., a Project Management Unit)? How will tasks and resources\n\u00A0\u00A0\u00A0 be managed?\n\n" +
      " • Risk Management: What are the key implementation risks (e.g., supply chain delays, construction challenges) and how do you plan to\n\u00A0\u00A0\u00A0 mitigate them?\n\n" +
      " • Stakeholder Engagement: How will you communicate with and involve key stakeholders (e.g., government agencies, local communities)\n\u00A0\u00A0\u00A0 throughout the project?\n\n" +
      "Even a brief draft covering these points will provide a strong basis for the analysis and enhancement.",
  },
  {
    q: (
      <>
        Tell Us About The Project’s Operations, Maintenance and Monitoring
        Below:
      </>
    ),
    result: <>Your Results Below:</>,
    placeholder:
      "Please provide the full text of your current Operational Sustainability and Performance Management section.\n\n" +
      "For the most helpful analysis, ensure your text includes your best attempt at describing:\n\n" +
      " • Long-Term Ownership: Who will own and be responsible for the project assets after the initial funding period ends?\n\n" +
      " • O&M Plan and Funding: How will ongoing operations and maintenance be managed, and more importantly, how will these long-term costs\n\u00A0\u00A0\u00A0 be funded?\n\n" +
      " • Performance Management: How will you track the project's long-term performance? What are the key metrics or Key Performance\n\u00A0\u00A0\u00A0 Indicators (KPIs)?\n\n" +
      " • Long-Term Resilience: How will the project be sustained against future challenges, such as climate impacts or technical issues?\n\n" +
      "Even a brief draft covering these points will provide a strong basis for the analysis and enhancement.",
    limit: 1000,
  },
];

export const sectionsMap = [
  {
    text: "Project Identification and Scope",
    section: "pis",
  },
  {
    text: "Feasibility and Site Assesment",
    section: "fsa",
  },
  {
    text: "Financial and Economic Analysis",
    section: "fea",
  },
  {
    text: "Regulatory and Compliance",
    section: "rc",
  },
  {
    text: "Project Implementation and Timeline",
    section: "it",
  },
  {
    text: "Operations Maintenance and Monitoring",
    section: "omm",
  },
];
const sections = sectionsMap.map((section) => section.section);

const AdvisorPage: FC<AdvisorFormProps> = () => {
  const nav = useNavigate();
  const socketRef = useRef(null);
  const { type, step } = useParams();
  const { advisorNarratives, setAdvisorNarratives } = useNarrativesContext();
  const [form] = useForm();
  const {
    user: { getSingleProjectData, projectNameData, loadGetAdvisorResults },
    modelType: { getSelectedModelType },
  } = useStore(null);

  const projectName =
    (projectNameData && JSON.parse(projectNameData).project_name) ||
    localStorage.getItem("projectName");

  const [initialInput, setInitialInput] = useState(null);
  const [isLoading, setIsLoading] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const [resultsData, setResultsData] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isAnswer, setIsAnswer] = useState(false);
  const [sectionOutputPresent, setSectionOutputPresent] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState(null);
  const [base64Images, setBase64Images] = useState<string[]>([]);

  const results = isSubmitting
    ? ""
    : advisorNarratives?.find((el) => el.section === sections[+step - 1])?.res;

  const email = localStorage.getItem("email");
  const isFormPage = type === "form";

  // useEffect(() => {
  //  const  data = {"section":"ch_15","questions":{"q1a":"jamaica","q1b":"adaptation","q2":"arar","q3":"zfrfrf"},"project_name":"first project","email":"mohamed.ardif456@gmail.com"}
  //   let socket = io(baseUrl,{
  //     extraHeaders: {
  //       "Origin": "*:*" // allowing connections with empty origin either
  //     },
  //   });
  //   // socket.set('origins', '*');
  //   socket.emit('climateRationale',data);
  //   socket.on('answer',(answer) => {
  //     console.log(answer);
  //   })
  // }, []);

  const handleSubmit = useCallback(
    async (saveOnly = false) => {
      if (!isValid) return;
      if (isFormPage) {
        setResultsData("");

        const values = form.getFieldsValue();

        if (saveOnly === true) {
          setIsSaving(true);
        } else {
          setIsSubmitting(true);
        }

        const data: DataType = {
          section: sections[+step - 1],
          input: values?.q,
          project_name: projectName,
          email: email,
          model: getSelectedModelType ?? "Titan",
        };

        if (saveOnly === true) {
          data.save_only = true;
        }

        if (base64Images) {
          data.input = {
            text: values?.q,
            images: base64Images,
          };
        }

        const socket = io(baseSocketUrl, {
          transports: ["websocket", "polling"],
        });

        socketRef.current = socket;
        socketRef?.current?.emit("projectStructuring", data);

        let timeoutId;
        if (saveOnly === true) {
          timeoutId = setTimeout(() => {
            socket.disconnect();
            setIsSaving(false);
            notification.error("An error occurred: Save request timed out.");
          }, 10000); // 10 seconds timeout
        }

        socketRef?.current?.on("answer", (answer) => {
          const isStopGeneration = localStorage.getItem("isStopGeneration");

          if (
            saveOnly === true &&
            answer === "User input is saved successfully"
          ) {
            clearTimeout(timeoutId); // Cancel timeout if response received
            socket.disconnect();
            setIsSaving(false);
            localStorage.removeItem("isStopGeneration");
            notification.success("Project saved successfully!");
            return nav(constRoute?.existingProject);
          }

          if (!isSubmitting && answer) {
            setIsSubmitting(true);
          }

          if (answer) {
            setIsAnswer(true);
          }

          if (isStopGeneration || answer === "#####") {
            socket.disconnect();
            setIsSubmitting(false);
            setIsAnswer(false);
            localStorage.removeItem("isStopGeneration");
            return;
          }

          form.setFieldValue("q", (form?.getFieldValue("q") ?? "") + answer);
          localStorage.setItem(
            "totalResultsAdvisor" + projectName,
            JSON.stringify({
              ...results,
              [`result${step}`]: (form?.getFieldValue("q") ?? "") + answer,
            }),
          );
        });
        socket.on("disconnect", () => {
          clearTimeout(timeoutId); // Prevent timeout from triggering after disconnect
          localStorage.removeItem("isStopGeneration");
          setIsSubmitting(false);
          setIsAnswer(false);
        });

        if (saveOnly !== true) {
          nav(constRoute?.advisorProject + "/results/" + step);
        }
      } else {
        if (saveOnly === true) {
          setIsSaving(true);
          await new Promise((resolve) => setTimeout(resolve, 3000));
          setIsSaving(false);
          notification.success("Project saved successfully!");
          return nav(constRoute?.existingProject);
        }

        if (formQuestions.length < +step + 1) {
          nav(constRoute?.gcfCongratulation + "/advisor");
          return;
        }
        setIsSubmitting(false);
        setIsAnswer(false);
        nav(constRoute?.advisorProject + "/form/" + (+step + 1));
      }
    },
    [type, step, getSelectedModelType, base64Images],
  );

  const handleGoBack = () => {
    socketRef?.current?.disconnect();
    setIsSubmitting(false);
    setIsAnswer(false);
    // if (isSubmitting) return;
    if (+step === 1 && type == "results") {
      nav(constRoute?.advisorProject + "/form/1");

      return;
    }

    if (+step < 2) {
      nav(constRoute.projectName + "/advisor");
      return;
    }

    if (type === "results") {
      nav(constRoute?.advisorProject + "/form/" + +step);
    } else {
      nav(constRoute?.advisorProject + "/results/" + (+step - 1));
    }
  };

  const handleSaveQuit = () => {
    handleSubmit(true);
  };

  const handleViewReport = () => {
    nav(constRoute?.advisorProject + "/results/" + +step);
  };

  const getProjectData = async () => {
    const data = {
      section: sections[+step - 1].toLowerCase(),
      project_name: projectName,
      functionality: "project structuring",
    };
    setIsLoading(true);
    setSectionOutputPresent(false);

    localStorage.setItem("data-for-tokens", JSON.stringify(data));
    await getSingleProjectData(data)
      .then((state) => {
        if (isFormPage) {
          form.setFieldValue("q", state?.questions.input);

          // TODO: a bug will bring you here
          if (state?.questions?.images) {
            setBase64Images(state?.questions?.images);
          }
        } else {
          if (!isSubmitting) {
            form.setFieldValue(`q`, state?.generated_narratives?.find(Boolean));
          }
        }
        setAdvisorNarratives((prev) =>
          prev.map((el) => {
            if (el.section === sections[+step - 1]) {
              const obj = {
                ...el,
                res:
                  state?.generated_narratives?.map((el) => el).join("") || "",
              };

              if (obj.res !== "") {
                setSectionOutputPresent(true);
              }

              return obj;
            } else {
              return el;
            }
          }),
        );
        setInitialInput(state?.questions);
      })
      .finally(() => setIsLoading(false));

    if (+step < 1 || formQuestions.length + 1 <= +step) {
      nav(constRoute?.gcfCongratulation + "/advisor");
    }
  };

  const handleStopGenerate = () => {
    if (socketRef) {
      socketRef.current.disconnect();
    }
    setIsSubmitting(false);
    setIsAnswer(false);
  };

  useEffect(() => {
    localStorage.removeItem("isStopGeneration");
    getProjectData();
  }, [step, type]);
  useEffect(() => {
    form.resetFields();
  }, [type === "results"]);

  useEffect(() => {
    socketRef?.current?.disconnect();
    form.resetFields();
    setIsSubmitting(false);
    setIsAnswer(false);
    setResultsData(
      advisorNarratives.find((el) => el.section === sections[+step - 1])?.res ||
        "",
    );
  }, [step]);

  useEffect(() => {
    return () => {
      socketRef?.current?.disconnect();
    };
  }, []);

  return (
    <div style={{ padding: "30px 52px 122px" }}>
      <AdvisorForm
        form={form}
        value={results}
        isSubmitting={isSubmitting}
        initialState={initialInput}
        handleSubmit={handleSubmit}
        isFormPage={isFormPage}
        Text={
          isFormPage
            ? formQuestions[+step - 1]?.q
            : formQuestions[+step - 1]?.result || results[`result${step}`] || ""
        }
        placeholder={formQuestions[+step - 1]?.placeholder}
        maxLength={formQuestions[+step - 1]?.limit}
        disabled={type === "results"}
        section={sections[+step - 1]}
        setIsValid={setIsValid}
        isValid={isValid}
        handleStopGenerate={handleStopGenerate}
        handleViewReport={handleViewReport}
        sectionOutputPresent={sectionOutputPresent}
        isLoading={isLoading}
        isAnswer={isAnswer}
        selectedFiles={selectedFiles}
        setSelectedFiles={setSelectedFiles}
        base64Images={base64Images}
        setBase64Images={setBase64Images}
      />
      <CommonFooterButton
        customStyle={{ left: 0 }}
        isLoadingSubmit={false}
        handleSubmit={handleSubmit}
        handlegoback={handleGoBack}
        isSubmitting={isSubmitting}
        isSaving={isSaving}
        handleSaveAndQuit={handleSaveQuit}
        isSubmit={type === "results"}
        // handleQuickNext={constRoute?.contextAndBaselineResults}
        form={form}
        setIsSubmitting={setIsSubmitting}
        disableSubmit={!isValid || isLoading}
      />
    </div>
  );
};

export default memo(observer(AdvisorPage));
